import { useLazyQuery } from '@apollo/client'
import { useMemo } from 'react'

import { navSearchAutocompleteQuery } from '../../app/_components/chrome/PrimaryNavSearch.client'
import { createPdpUrl } from '../../app/_components/navigation/createPdpUrl'
import {
  derivePricingForProductGroup,
  derivePricingForVariantGroup,
} from '../../app/_components/pricing/PricingRange.client'
import { useLocalization } from '../../app/_providers/LocalizationProvider.client'
import { SearchAutocompleteProduct } from '../../types/graphql/search'

export function useSearchAutocomplete() {
  const [fetchSearchAutocompleteResults, { data, previousData, error, loading }] = useLazyQuery(
    navSearchAutocompleteQuery,
    { notifyOnNetworkStatusChange: true }
  )
  const { region } = useLocalization()

  const products: SearchAutocompleteProduct[] = useMemo(() => {
    if (data?.productCatalogSearch?.nodes) {
      return data.productCatalogSearch.nodes.map(node => {
        const variantGroup = node.variantGroup
        return {
          id: variantGroup.id,
          sku: variantGroup.variant.sku,
          categoryKey: variantGroup.primaryCategory.id,
          deprecatedProductHandle: `${variantGroup.product.handle}${
            variantGroup.color?.id ? '-' : ''
          }${variantGroup.color?.id}`,
          colorKey: variantGroup.color?.id,
          productGroupHandle: variantGroup.product.handle,
          shopifyId: variantGroup.variant.shopifyProductId,
          title: variantGroup.seoTitle,
          url: variantGroup.urlPath,
          imageSrc: variantGroup.images?.[0]?.url,
          pricing: derivePricingForVariantGroup(variantGroup),
          price: variantGroup.variant.price.amount,
          discountPrice: variantGroup.variant.compareAtPrice?.amount
            ? variantGroup.variant.price.amount
            : undefined,
          currencyCode: variantGroup.variant.price.currencyCode,
        }
      })
    } else {
      return (data?.search.items.nodes ?? []).map(node => {
        const product = node.product
        return {
          id: product.id,
          sku: product.defaultVariant.sku,
          categoryKey: product.rawCategory,
          deprecatedProductHandle: product.handle ?? product.productGroup.handle,
          colorKey: product.colorInfo?.rawName,
          productGroupHandle: product.productGroup.handle,
          shopifyId: product.shopifyId,
          title: product.seoTitle?.trim().length ? product.seoTitle : product.title,
          url: createPdpUrl({
            productGroupHandle: product.productGroup.handle,
            isExclusive: product.exclusive,
            isKit: product.rawCategory === 'Product Kit',
            genderKey: product.genderCategory,
            collectionHandle: null,
            colorKey: product.colorInfo?.rawName,
            fitKey: product.rawFit,
            sizeKey: product.defaultVariant.rawSize,
          }),
          imageSrc: product.images?.nodes[0]?.source,
          pricing: product.defaultVariant.priceDetails?.price
            ? derivePricingForProductGroup(
                {
                  products: [
                    {
                      priceRange: product.productGroup.priceRange,
                      defaultVariant: {
                        price: product.defaultVariant.priceDetails.price.amount,
                        discountPrice: product.defaultVariant.priceDetails.discountPrice?.amount,
                      },
                    },
                  ],
                },
                region.currency
              )
            : undefined,
          price: (product.defaultVariant.priceDetails!.discountPrice?.amount ??
            product.defaultVariant.priceDetails!.price!.amount) as number,
          discountPrice: product.defaultVariant.priceDetails!.discountPrice?.amount as
            | number
            | undefined,
          currencyCode: product.defaultVariant.priceDetails!.price!.currency,
        }
      })
    }
  }, [data?.productCatalogSearch?.nodes, data?.search.items.nodes, region.currency])

  const suggestions: string[] = useMemo(() => {
    const v2Suggestions =
      data?.productCatalogSearchSuggester?.nodes.map(suggestionNode => suggestionNode.query!) ??
      previousData?.productCatalogSearchSuggester?.nodes.map(
        suggestionNode => suggestionNode.query!
      ) ??
      []

    const v1Suggestions =
      data?.search?.suggestions?.map(suggestion => suggestion.query) ??
      previousData?.search?.suggestions?.map(suggestion => suggestion.query) ??
      []

    if (v2Suggestions.length > 0) {
      return v2Suggestions
    } else {
      return v1Suggestions
    }
  }, [data, previousData])

  const queryRan: boolean = !!data || !!error
  const noResult: boolean = !loading && products.length === 0 && queryRan

  return { fetchSearchAutocompleteResults, products, suggestions, error, noResult }
}
